'use client';

import { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import TextInput from "../../components/Forms/TextInput.jsx";
import TextareaInput from "../../components/Forms/TextareaInput.jsx";
import { StyledButton } from "../../components/Buttons/StyledButton.jsx";
import axiosRequest from "../../api/axiosRequest";
//import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import SelectInput from "../../components/Forms/SelectInput.jsx";
import PhoneNumberInput from "../../components/Forms/PhoneNumberInput";

const CollaborationForm = () => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  // const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (values, { resetForm }) => {
    // if (!executeRecaptcha) {
    //   console.warn("Recaptcha not yet available");
    //   return;
    // }
    // const token = await executeRecaptcha("CollaborationForm");
    const formData = new FormData();

    formData.append("institution_name", values.institution_name);
    formData.append("institution_type", values.institution_type);
    formData.append("location", values.location);
    formData.append("contact_name", values.contact_person);
    formData.append("designation", values.designation);
    formData.append("email", values.email);
    formData.append("phone_number", values.contact_number);
    formData.append("collaboration_purpose", values.collaboration_purpose);
    formData.append("additional_message", values.additional_notes);
    //formData.append("recaptcha_token", token);

    try {
      setLoading(true);
      const response = await axiosRequest(
        "public/add-collaboration-information",
        "POST",
        formData,
        "multipart/form-data;"
      );

      if (response.data) {
        setSuccessMessage("Thank you! We will contact you soon.");
        //toast.success("Thank you! We will contact you soon.")
        resetForm();
      } else {
        setSuccessMessage("");
        toast.error(response?.data?.message || "Submission failed.");
      }
    } catch (error) {
      setSuccessMessage("");
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    institution_name: Yup.string().required("Institution Name is required"),
    institution_type: Yup.string()
      .oneOf(["1", "2", "3", "4", "4", "5"], "Select a valid Institution Type")
      .required("Institution Type is required"),
    location: Yup.string().required("Location is required"),
    contact_person: Yup.string().required("Full Name is required"),
    designation: Yup.string().required("Designation is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    contact_number: Yup.string()
      .matches(/^\+\d{1,4}\s?\d{4,14}$/, "Enter a valid phone number")
      .required("Phone number is required"),
    collaboration_purpose: Yup.string()
      .oneOf(["1", "2", "3", "4", "5"], "Select a valid Purpose")
      .required("Purpose of Collaboration is required"),
    terms: Yup.boolean()
      .oneOf([true], "You must agree to the terms and conditions")
      .required("You must agree to the terms and conditions"),
  });

  return (
    <div className="relative max-w-[796px] mx-auto rounded-lg mt-5">
      <Formik
        initialValues={{
          institution_name: "",
          institution_type: 1,
          location: "",
          contact_person: "",
          designation: "",
          email: "",
          contact_number: "",
          collaboration_purpose: 2,
          additional_notes: "",
          terms: true,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form className="flex flex-col gap-6">
            <h3 className="text-xl font-semibold">
              Institution/Organization Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <TextInput
                  label="Institution/Company Name*"
                  id="institution_name"
                  name="institution_name"
                  value={values.institution_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.institution_name && errors.institution_name}
                />
                {errors.institution_name && touched.institution_name && (
                  <div className="text-red-500 text-xs mt-2">
                    {errors.institution_name}
                  </div>
                )}
              </div>
              <div>
                <SelectInput
                  label="Type of Institution*"
                  id="institution_type"
                  name="institution_type"
                  value={values.institution_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={[
                    { value: 1, label: "College" },
                    { value: 2, label: "School" },
                    {
                      value: 3,
                      label: "IT Training Center",
                    },
                    { value: 4, label: "University" },
                    { value: 5, label: "Corporate" },
                    { value: 6, label: "Other" },
                  ]}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <TextInput
                  label="Location (Address)*"
                  id="location"
                  name="location"
                  value={values.location}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.location && errors.location}
                />
                {errors.location && touched.location && (
                  <div className="text-red-500 text-xs mt-2">
                    {errors.location}
                  </div>
                )}
              </div>
            </div>

            <h3 className="text-xl font-semibold ">Contact Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <TextInput
                  label="Full Name*"
                  id="contact_person"
                  name="contact_person"
                  value={values.contact_person}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contact_person && errors.contact_person}
                />
                {errors.contact_person && touched.contact_person && (
                  <div className="text-red-500 text-xs mt-2">
                    {errors.contact_person}
                  </div>
                )}
              </div>
              <div>
                <TextInput
                  label="Designation*"
                  id="designation"
                  name="designation"
                  value={values.designation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.designation && errors.designation}
                />
                {errors.designation && touched.designation && (
                  <div className="text-red-500 text-xs mt-2">
                    {errors.designation}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <TextInput
                  label="Official Email*"
                  id="email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                />
                {errors.email && touched.email && (
                  <div className="text-red-500 text-xs mt-2">
                    {errors.email}
                  </div>
                )}
              </div>
              <div>
                <PhoneNumberInput
                  label="Phone Number*"
                  id="contact_number"
                  name="contact_number"
                  type="number"
                  value={values.contact_number}
                  onChange={handleChange}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  error={touched.contact_number && errors.contact_number}
                />
                {errors.contact_number && touched.contact_number && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.contact_number}
                  </div>
                )}
              </div>
              {/* <div>
                <TextInput
                  label="Phone Number*"
                  id="contact_number"
                  name="contact_number"
                  value={values.contact_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contact_number && errors.contact_number}
                />
                {errors.contact_number && touched.contact_number && (
                  <div className="text-red-500 text-xs mt-2">
                    {errors.contact_number}
                  </div>
                )}
              </div> */}
            </div>

            <h3 className="text-xl font-semibold mt-6">
              Collaboration Interest
            </h3>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
              <div>
                <SelectInput
                  label="Purpose of Collaboration*"
                  id="collaboration_purpose"
                  name="collaboration_purpose"
                  value={values.collaboration_purpose}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={[
                    { value: 1, label: "Internship" },
                    {
                      value: 2,
                      label: "Industrial Training",
                    },
                    { value: 3, label: "Workshop" },
                    { value: 4, label: "Recruitment" },
                    { value: 5, label: "Other" },
                  ]}
                  error={
                    touched.collaboration_purpose &&
                    errors.collaboration_purpose
                  }
                />
              </div>
            </div>

            <TextareaInput
              label="Additional Message"
              id="additional_notes"
              name="additional_notes"
              rows={3}
              value={values.additional_notes}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            <div>
              <div>
                <p className="text-sm text-color-2 -mt-6">
                  By submitting, you agree to our{" "}
                  <a
                    href="/info/terms"
                    className="text-color-1 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    terms & conditions
                  </a>
                  .
                </p>

                {touched.terms && errors.terms && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.terms}
                  </div>
                )}
              </div>
              {touched.terms && errors.terms && (
                <div className="text-red-500 text-xs mt-1">{errors.terms}</div>
              )}
            </div>
            {successMessage && (
              <div className="bg-green-100 text-green-800 p-4 rounded-md -mt-3 flex items-center justify-between">
                <span>{successMessage}</span>
                <button
                  type="button"
                  className="text-red-500 font-bold"
                  onClick={() => setSuccessMessage("")}
                >
                  ✕
                </button>
              </div>
            )}
            <StyledButton
              text="Submit Request"
              type="submit"
              loading={loading}
              onClick={handleSubmit}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CollaborationForm;
